import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Chip, 
  InputBase,
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Stack
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../utils/textUtils';

// Status and priority color mapping
const statusColors = {
  not_started: '#9e9e9e',
  in_progress: '#2196f3',
  completed: '#4caf50'
};

const priorityColors = {
  low: '#8bc34a',
  medium: '#ff9800',
  high: '#f44336'
};

// Status and priority labels
const statusLabels = {
  not_started: t('BELUM DIMULAI'),
  in_progress: t('DALAM PROSES'),
  completed: t('SELESAI')
};

const priorityLabels = {
  low: t('RENDAH'),
  medium: t('SEDANG'),
  high: t('TINGGI')
};

const Tasks = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentTask, setCurrentTask] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'not_started',
    priority: 'medium',
    dueDate: null,
    assignedToId: '',
    projectId: ''
  });

  // Fetch tasks, projects, and users on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch tasks
        const tasksResponse = await axios.get('/api/tasks', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Fetch projects
        const projectsResponse = await axios.get('/api/projects', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Fetch users
        const usersResponse = await axios.get('/api/users', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setTasks(tasksResponse.data.tasks || []);
        setProjects(projectsResponse.data.projects || []);
        setUsers(usersResponse.data.users || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        enqueueSnackbar('Gagal memuat data tugas', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token, enqueueSnackbar]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Filter tasks based on tab
    switch (newValue) {
      case 0: // All tasks
        setFilterStatus('');
        break;
      case 1: // Not started
        setFilterStatus('not_started');
        break;
      case 2: // In progress
        setFilterStatus('in_progress');
        break;
      case 3: // Completed
        setFilterStatus('completed');
        break;
      default:
        setFilterStatus('');
    }
  };

  // Handle dialog open for create
  const handleCreateTask = () => {
    setDialogMode('create');
    setFormData({
      title: '',
      description: '',
      status: 'not_started',
      priority: 'medium',
      dueDate: null,
      assignedToId: '',
      projectId: ''
    });
    setOpenDialog(true);
  };

  // Handle dialog open for edit
  const handleEditTask = (task) => {
    setDialogMode('edit');
    setCurrentTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      dueDate: task.dueDate ? new Date(task.dueDate) : null,
      assignedToId: task.assignedToId || '',
      projectId: task.projectId || ''
    });
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentTask(null);
  };

  // Handle form field change
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  // Handle date change
  const handleDateChange = (date) => {
    setFormData((prevData) => ({
      ...prevData,
      dueDate: date
    }));
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        // Create task
        const response = await axios.post('/api/tasks', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setTasks((prevTasks) => [...prevTasks, response.data]);
        enqueueSnackbar('Tugas berhasil dibuat', { variant: 'success' });
      } else {
        // Update task
        const response = await axios.put(`/api/tasks/${currentTask.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setTasks((prevTasks) => 
          prevTasks.map((task) => (task.id === currentTask.id ? response.data : task))
        );
        enqueueSnackbar('Tugas berhasil diperbarui', { variant: 'success' });
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error submitting task:', error);
      enqueueSnackbar('Gagal menyimpan tugas', { variant: 'error' });
    }
  };

  // Handle task deletion
  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus tugas ini?')) {
      try {
        await axios.delete(`/api/tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
        enqueueSnackbar('Tugas berhasil dihapus', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting task:', error);
        enqueueSnackbar('Gagal menghapus tugas', { variant: 'error' });
      }
    }
  };

  // Filter tasks based on status, priority, project, and search query
  const filteredTasks = tasks.filter((task) => {
    // Filter by status
    if (filterStatus && task.status !== filterStatus) {
      return false;
    }
    
    // Filter by priority
    if (filterPriority && task.priority !== filterPriority) {
      return false;
    }
    
    // Filter by project
    if (filterProject && task.projectId !== parseInt(filterProject)) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        task.title.toLowerCase().includes(query) ||
        (task.description && task.description.toLowerCase().includes(query))
      );
    }
    
    return true;
  });

  const statusCounts = tasks.reduce(
    (acc, task) => {
      const status = task.status || 'not_started';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    { not_started: 0, in_progress: 0, completed: 0 }
  );

  const priorityCounts = tasks.reduce(
    (acc, task) => {
      const priority = task.priority || 'medium';
      acc[priority] = (acc[priority] || 0) + 1;
      return acc;
    },
    { low: 0, medium: 0, high: 0 }
  );

  // DataGrid columns
  const columns = [
    { field: 'id', headerName: t('ID'), width: 70 },
    { field: 'title', headerName: t('JUDUL'), flex: 1 },
    {
      field: 'status',
      headerName: t('STATUS'),
      width: 150,
      renderCell: (params) => (
        <Chip
          label={statusLabels[params.value]}
          style={{
            backgroundColor: statusColors[params.value],
            color: '#fff'
          }}
          size="small"
        />
      )
    },
    {
      field: 'priority',
      headerName: t('PRIORITAS'),
      width: 150,
      renderCell: (params) => (
        <Chip
          label={priorityLabels[params.value]}
          style={{
            backgroundColor: priorityColors[params.value],
            color: '#fff'
          }}
          size="small"
        />
      )
    },
    {
      field: 'dueDate',
      headerName: t('TENGGAT WAKTU'),
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleDateString('id-ID');
      }
    },
    {
      field: 'assignedTo',
      headerName: t('DITUGASKAN KEPADA'),
      width: 180,
      valueGetter: (params) => {
        return params.row.assignedTo ? params.row.assignedTo.fullName : '-';
      }
    },
    {
      field: 'project',
      headerName: t('PROYEK'),
      width: 180,
      valueGetter: (params) => {
        return params.row.project ? params.row.project.name : '-';
      }
    },
    {
      field: 'actions',
      headerName: t('AKSI'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('EDIT')}>
            <IconButton
              size="small"
              onClick={() => handleEditTask(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('HAPUS')}>
            <IconButton
              size="small"
              onClick={() => handleDeleteTask(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: { xs: 1.5, sm: 2.5, md: 3 }, bgcolor: '#F8FAFC' }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: 2,
          borderColor: 'divider',
          bgcolor: '#FFFFFF'
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {t('MANAJEMEN TUGAS')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('KELOLA TUGAS HARIAN DAN MINGGUAN UNTUK PROYEK-PROYEK KONSTRUKSI PREFABRIKASI.')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTask}
            sx={{ bgcolor: '#0f172a', '&:hover': { bgcolor: '#0b1324' }, fontWeight: 700 }}
          >
            {t('TAMBAH TUGAS')}
          </Button>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))'
          },
          gap: 2,
          mb: 3
        }}
      >
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
          <Typography variant="caption" color="text.secondary">{t('BELUM DIMULAI')}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{statusCounts.not_started}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
          <Typography variant="caption" color="text.secondary">{t('DALAM PROSES')}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{statusCounts.in_progress}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
          <Typography variant="caption" color="text.secondary">{t('SELESAI')}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{statusCounts.completed}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
          <Typography variant="caption" color="text.secondary">{t('PRIORITAS TINGGI')}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{priorityCounts.high}</Typography>
        </Paper>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 3,
          borderRadius: 2,
          borderColor: 'divider',
          bgcolor: '#FFFFFF'
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <Paper
            component="form"
            variant="outlined"
            sx={{
              px: 1.5,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              width: { xs: '100%', md: 320 },
              borderRadius: 2,
              borderColor: 'divider'
            }}
          >
            <SearchIcon fontSize="small" />
            <InputBase
              sx={{ ml: 1, flex: 1, fontSize: '0.9rem' }}
              placeholder={t('CARI TUGAS')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Paper>

          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('PRIORITAS')}</InputLabel>
              <Select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                label={t('PRIORITAS')}
              >
                <MenuItem value="">{t('SEMUA')}</MenuItem>
                <MenuItem value="low">{t('RENDAH')}</MenuItem>
                <MenuItem value="medium">{t('SEDANG')}</MenuItem>
                <MenuItem value="high">{t('TINGGI')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('PROYEK')}</InputLabel>
              <Select
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
                label={t('PROYEK')}
              >
                <MenuItem value="">{t('SEMUA PROYEK')}</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => {
                setFilterStatus('');
                setFilterPriority('');
                setFilterProject('');
                setSearchQuery('');
                setTabValue(0);
              }}
              sx={{ borderColor: '#CBD5F5', color: '#0f172a', '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' } }}
            >
              {t('RESET FILTER')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ width: '100%', mb: 3, borderRadius: 2, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
            '& .MuiTabs-indicator': { backgroundColor: '#0f172a' }
          }}
        >
          <Tab label={t('SEMUA')} />
          <Tab label={t('BELUM DIMULAI')} />
          <Tab label={t('DALAM PROSES')} />
          <Tab label={t('SELESAI')} />
        </Tabs>
      </Paper>

      <Paper variant="outlined" sx={{ width: '100%', height: 520, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredTasks}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            density="standard"
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#F8FAFC',
                borderBottom: '1px solid #e2e8f0'
              },
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-row:hover': { backgroundColor: '#F8FAFC' }
            }}
          />
        )}
      </Paper>
      
      {/* Create/Edit Task Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? t('TAMBAH TUGAS BARU') : t('EDIT TUGAS')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('JUDUL TUGAS')}
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('DESKRIPSI')}
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('STATUS')}</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  label={t('STATUS')}
                >
                  <MenuItem value="not_started">{t('BELUM DIMULAI')}</MenuItem>
                  <MenuItem value="in_progress">{t('DALAM PROSES')}</MenuItem>
                  <MenuItem value="completed">{t('SELESAI')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('PRIORITAS')}</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleFormChange}
                  label={t('PRIORITAS')}
                >
                  <MenuItem value="low">{t('RENDAH')}</MenuItem>
                  <MenuItem value="medium">{t('SEDANG')}</MenuItem>
                  <MenuItem value="high">{t('TINGGI')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('TENGGAT WAKTU')}
                  value={formData.dueDate}
                  onChange={handleDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('DITUGASKAN KEPADA')}</InputLabel>
                <Select
                  name="assignedToId"
                  value={formData.assignedToId}
                  onChange={handleFormChange}
                  label={t('DITUGASKAN KEPADA')}
                >
                  <MenuItem value="">{t('TIDAK ADA')}</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('PROYEK')}</InputLabel>
                <Select
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleFormChange}
                  label={t('PROYEK')}
                >
                  <MenuItem value="">{t('TIDAK ADA')}</MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('BATAL')}</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {dialogMode === 'create' ? t('TAMBAH') : t('SIMPAN')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tasks;
